import type { MouseEventHandler } from 'react';

import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { I18nService } from '@shared/types/i18n';

import STYLES from './ReviewImage.module.scss';

import { buildImageAltText } from '@hotels-hotel-detail/utils/review-service';
import ProgressivePictureButton from '@shared/ui/common/ProgressivePictureButton';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

const mobileSrcSets = (image: string) => [
  `${image.replace('_WxH', '_120x90')} 1x`,
  `${image.replace('_WxH', '_240x180')} 2x`,
  `${image.replace('_WxH', '_480x360')} 4x`,
];

const tabletSrcSets = (image: string) => [
  `${image.replace('_WxH', '_140x105')} 1x`,
  `${image.replace('_WxH', '_280x210')} 2x`,
  `${image.replace('_WxH', '_560x420')} 4x`,
];

const desktopSrcSets = (image: string) => [
  `${image.replace('_WxH', '_161x122')} 1x`,
  `${image.replace('_WxH', '_322x244')} 2x`,
  `${image.replace('_WxH', '_644x488')} 4x`,
];

const tabletWidth = 750;
const desktopWidth = 1048;

type Props = {
  src: string;
  useSmallerImage?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  i18n: I18nService;
  reviewIndex?: number;
  imageIndex: number;
};

const ReviewImage = ({
  i18n,
  imageIndex,
  onClick,
  reviewIndex,
  src,
  useSmallerImage = false,
}: Props) => {
  const ariaLabel = buildImageAltText({
    reviewIndex,
    imageIndex,
    i18n,
  });

  return (
    <ProgressivePictureButton
      lazyLoad
      sources={[
        {
          srcSet: mobileSrcSets(src),
          media: `(max-width: ${tabletWidth - 1}px)`,
        },
        {
          srcSet: tabletSrcSets(src),
          media: `(min-width: ${tabletWidth}px) and (max-width: ${
            desktopWidth - 1
          }px)`,
        },
        {
          srcSet: desktopSrcSets(src),
          media: `(min-width: ${desktopWidth}px)`,
        },
      ]}
      containerButtonClassName={cls(
        'ReviewImage__containerButton',
        useSmallerImage
          ? 'ReviewImage__containerButtonMobileSmallerSize'
          : 'ReviewImage__containerButtonMobileSize',
      )}
      className={cls(
        'ReviewImage__img',
        !src.includes('_WxH') && 'ReviewImage__imgSize',
      )}
      onClick={onClick}
      src={src}
      ariaLabel={ariaLabel}
      alt=""
    />
  );
};

export default withI18n(ReviewImage);
